/**
 * useShareReport Hook
 *
 * React Query mutation hook for sharing the report with investors (one-way door #2).
 * Freezes the report and transitions to SHARED state (terminal).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useTenant } from "@/contexts/TenantContext";
import type { Assessment } from "./useAssessments";

export function useShareReport(assessmentId: string) {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();

  return useMutation<Assessment, Error, void>({
    mutationFn: async () => {
      if (!currentTenant) {
        throw new Error("No tenant selected");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/assessments/${assessmentId}/share-report` as any;
      const { data, error } = await apiClient.POST(url);

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const status = (error as any)?.status;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const message = (error as any)?.message || "Failed to share report";

        if (status === 409) {
          const errorObj = new Error(message);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (errorObj as any).status = 409;
          throw errorObj;
        }

        if (status === 404) {
          throw new Error("Assessment not found");
        }

        throw new Error(message);
      }

      return data as Assessment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assessment", currentTenant?.id, assessmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["assessments", currentTenant?.id],
      });
    },
  });
}
